import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { projectsAPI } from '../api';

function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    short_description: '',
    description: '',
    is_public: true
  });
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await projectsAPI.getMyProjects();
      setProjects(response.data.projects);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setMessage('');

    try {
      const response = await projectsAPI.create(formData);
      if (response.data.success) {
        setMessage('Project created successfully!');
        setFormData({
          title: '',
          short_description: '',
          description: '',
          is_public: true
        });
        setShowCreateModal(false);
        loadProjects();
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project? This will not delete the files.')) return;

    try {
      await projectsAPI.delete(projectId);
      loadProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="container">
      <div className="d-flex justify-between align-center mb-3">
        <h1>My Projects</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          + Create Project
        </button>
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New Project</h2>
              <button
                className="btn-remove"
                onClick={() => setShowCreateModal(false)}
              >
                ✕
              </button>
            </div>

            {message && (
              <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-error'}`}>
                {message}
              </div>
            )}

            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label className="form-label">Project Title *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="My Awesome App"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Short Description</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.short_description}
                  onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                  placeholder="A brief one-liner about your project"
                  maxLength="100"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description of your project..."
                  rows="4"
                ></textarea>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_public}
                    onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                    className="checkbox-input"
                  />
                  <span className="checkbox-text">
                    <span className="checkbox-title">Make project public</span>
                    <span className="checkbox-description">
                      Anyone can view and download files from this project
                    </span>
                  </span>
                </label>
              </div>

              <div className="d-flex gap-2 mt-3">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create Project'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Projects List */}
      <div className="card">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : projects.length === 0 ? (
          <div className="empty-state">
            <h3>No projects yet</h3>
            <p className="text-muted">
              Create your first project to organize and share your files
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary mt-2"
            >
              Create Your First Project
            </button>
          </div>
        ) : (
          <div className="projects-grid">
            {projects.map((project) => (
              <div key={project.project_id} className="project-card">
                <div className="project-card-header">
                  <h3 className="project-title">
                    <Link to={`/projects/${project.project_id}`}>
                      {project.title}
                    </Link>
                  </h3>
                  <span className={`badge ${project.is_public ? 'badge-success' : 'badge-warning'}`}>
                    {project.is_public ? 'Public' : 'Private'}
                  </span>
                </div>

                {project.short_description && (
                  <p className="project-description">{project.short_description}</p>
                )}

                <div className="project-stats">
                  <div className="stat">
                    <span className="stat-value">{project.stats.total_downloads}</span>
                    <span className="stat-label">downloads</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{project.stats.views}</span>
                    <span className="stat-label">views</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{project.downloads.length}</span>
                    <span className="stat-label">files</span>
                  </div>
                </div>

                <div className="project-footer">
                  <span className="text-small text-muted">
                    Updated {formatDate(project.updated_at)}
                  </span>
                  <div className="d-flex gap-1">
                    <Link
                      to={`/projects/${project.project_id}`}
                      className="btn btn-primary btn-sm"
                    >
                      Manage
                    </Link>
                    <button
                      onClick={() => handleDelete(project.project_id)}
                      className="btn btn-danger btn-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Projects;
